import { Component, OnInit, OnDestroy, EventEmitter, Input } from "@angular/core";
import { MatDialogRef, MatDialog } from "@angular/material/dialog";
import { PageEvent } from "@angular/material/paginator";
import { Sort } from "@angular/material/sort";
import { ActivatedRoute, Router } from "@angular/router";
import { ConfigService } from "@app/core/config/config.service";
import { AuthenticationInfoService } from "@app/core/guards/authentication-info.service";
import { TableColumnsConfigHelper } from "@app/core/models/configuration/table-columns-config-helper";
import { TableColumnsConfigKey } from "@app/core/models/configuration/table-columns-config-key.model";
import { ImportMissingInvoiceNumberDataSource } from "@app/core/models/imports/import-missing-invoice-number-data-source.model";
import { ImportMissingInvoiceNumberDocumentSearchCriteriaModel, ImportMissingInvoiceNumberDocumentSearchCriteriaModelIsPickedStatuses } from "@app/core/models/imports/import-missing-invoice-number-searchcriteria.model";
import { ImportMissingInvoiceNumberModel } from "@app/core/models/imports/import-missing-invoice-number.model";
import { SearchFilterModel } from "@app/core/models/search-filter.model";
import { ReferentialItem } from "@app/core/models/shared/referential-item.model";
import { FileDownloadService } from "@app/core/services/filedownload.service";
import { HttpCancelService } from "@app/core/services/http-cancel.service";
import { ImportService } from "@app/core/services/import.service";
import { SessionStorageService } from "@app/core/services/session-storage.service";
import { ModalComponent } from "@app/shared/components/modal/modal.component";
import { ProgressComponent } from "@app/shared/components/progress/progress.component";
import { ColumnDefinitionModel } from "@app/shared/models/column-definition.model";
import { DropdownHelper } from "@app/shared/models/dropdown-helper.model";
import { ModalHelper } from "@app/shared/models/modal-helper.model";
import { MultiSelectModel } from "@app/shared/models/multi-select.model";
import { TableRowAction, TableMassAction, TableIndication, TableActionIcon } from "@app/shared/models/table-action-type.model";
import { TableNavigationModel } from "@app/shared/models/table-navigation.model";
import { v4 as uuidv4 } from 'uuid';
import * as _ from "lodash";
import * as moment from "moment";
import { IDropdownSettings } from "ng-multiselect-dropdown";
import { Subject, BehaviorSubject } from "rxjs";
import { takeUntil, debounceTime } from "rxjs/operators";
import { AnalysisMissingInvoiceNumberDetailComponent } from "./detail/analysis-missing-invoice-number-detail.component";

@Component({
    selector: 'app-analysis-missing-invoice-number-search',
    templateUrl: './analysis-missing-invoice-number.component.html',
    styleUrls: ['./analysis-missing-invoice-number.component.scss']
})
export class AnalysisMissingInvoiceNumberComponent implements OnInit, OnDestroy {
    importMissingInvoiceNumberDataSource: ImportMissingInvoiceNumberDataSource;
    data: ImportMissingInvoiceNumberModel[] = [];
    tableActions: TableRowAction[] = [];
    tableMassActions: TableMassAction[] = [];
    tableIndications: TableIndication[] = [];
    importMissingInvoiceNumberDocumentSearchCriteriaModel: ImportMissingInvoiceNumberDocumentSearchCriteriaModel;
    debounceTime = 500;
    filterStorageKey: string;

    highlights: any[] = [];

    private searchInputChanged: Subject<string> = new Subject<string>();
    private unsubscribe$: Subject<boolean> = new Subject<boolean>();

    isPickedDropdownList: ReferentialItem[] = [];
    isPickedSelectedItems: ReferentialItem[] = [];

    dropdownSettings: IDropdownSettings = {};
    panelOpenState: boolean = true;
    sortColumn: string = '';
    sortDirection: string = '';
    pageIndex: number = 0;
    pageSize: number = 20;
    paginatorLength: number = 10;
    tablePreferenceConfigKey: string;

    configKey: string = TableColumnsConfigKey.MISSINGINVOICENUMBERS;
    exportFileName: string;
    singleSelectionWithoutSearchDropdownSettings: IDropdownSettings = {};

    private massDownloadRequestSubject$ = new BehaviorSubject<string>(null);
    public massDownloadRequest$ = this.massDownloadRequestSubject$.asObservable();
    progressDialog: MatDialogRef<ProgressComponent>;

    selectedIds: number[] = [];
    isAllSelected: boolean = false;
    displayedColumnsLabel: ColumnDefinitionModel[] = [];

    @Input() onDataSourceChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() onExportDataSourceAvailable: EventEmitter<any> = new EventEmitter<any>();

    public onFiltersCleared: EventEmitter<void> = new EventEmitter<void>();
    public onExportExcelCaller: EventEmitter<void> = new EventEmitter<void>();

    title: string;

    constructor(
        public dialog: MatDialog,
        private route: ActivatedRoute,
        private importService: ImportService,
        private fileDownloadService: FileDownloadService,
        private dropdownHelper: DropdownHelper,
        private multiSelectModel: MultiSelectModel,
        private sessionStorageService: SessionStorageService,
        private httpCancelService: HttpCancelService,
        private authenticationInfoService: AuthenticationInfoService,
        private config: ConfigService,
        private router: Router,
    ) {
        this.importMissingInvoiceNumberDocumentSearchCriteriaModel = new ImportMissingInvoiceNumberDocumentSearchCriteriaModel();
        this.exportFileName = 'Documents_Erreur';
        this.title = "DOCUMENTS A ANALYSER";
    }

    ngOnInit(): void {
        this.filterStorageKey = this.route.snapshot.data.searchFilterKey;
        this.tablePreferenceConfigKey = "table_config_missingInvoiceNumberSearch";
        this.route.data.subscribe(data => {
            this.importMissingInvoiceNumberDataSource = data.dataSource;
            this.data = data.dataSource.Items;
            this.paginatorLength = data.dataSource.TotalItems;
        });

        this.searchInputChanged
            .pipe(takeUntil(this.unsubscribe$),
                debounceTime(this.debounceTime))
            .subscribe(() => {
                this.onItemSelect();
            });

        this.singleSelectionWithoutSearchDropdownSettings = this.multiSelectModel.getSingleSelection(true);

        this.isPickedDropdownList = this.dropdownHelper.getYesOrNoDropdownList();

        this.tableActions.push(new TableRowAction(TableActionIcon.ZOOM, 'Voir le détail du document', this.onZoom));
        this.tableActions.push(new TableRowAction(TableActionIcon.PDF, 'Voir le fichier', this.onPdfViewer));
        this.tableActions.push(new TableRowAction(TableActionIcon.DOWNLOAD, 'Télécharger le document', this.onDownload));
        this.tableActions.push(new TableRowAction(TableActionIcon.INJECT, 'Réinjecter la facture', this.onInject, this.ensureCanBeInjected));

        this.tableIndications.push(new TableIndication(TableActionIcon.ADJUST, "A traiter", (importMissingInvoiceNumberDocument: ImportMissingInvoiceNumberModel) => _.isNil(importMissingInvoiceNumberDocument.PickedByUserId), 'indic-red'));
        this.tableIndications.push(new TableIndication(TableActionIcon.ADJUST, "Traité", (importMissingInvoiceNumberDocument: ImportMissingInvoiceNumberModel) => !_.isNil(importMissingInvoiceNumberDocument.PickedByUserId) && importMissingInvoiceNumberDocument.PickedByUserId !== this.authenticationInfoService.authentificatedUser.Id, 'indic-blue'));
        this.tableIndications.push(new TableIndication(TableActionIcon.ADJUST, "Traité par moi", (importMissingInvoiceNumberDocument: ImportMissingInvoiceNumberModel) => importMissingInvoiceNumberDocument.PickedByUserId === this.authenticationInfoService.authentificatedUser.Id, 'indic-green'));

        this.tableMassActions.push(new TableMassAction(TableActionIcon.PROCESS, "Traiter", this.onMassPick));
        this.tableMassActions.push(new TableMassAction(TableActionIcon.CANCELPROCESS, "Relâcher", this.onMassUnpick, this.ensureAllArePickedByMe));

        this.restoreFilters();

        this.displayedColumnsLabel = new TableColumnsConfigHelper().getColumnsDefinition(this.configKey);

        this.massDownloadRequest$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((requestId: string) => {
                if (_.isNil(requestId)) {
                    if (!_.isNil(this.progressDialog)) {
                        this.progressDialog.close(false);
                    }
                    return;
                }

                const progressModal = new ModalHelper().getProgressConfig(requestId);
                this.progressDialog = this.dialog.open(ProgressComponent, progressModal);

                this.progressDialog.afterClosed()
                    .pipe(takeUntil(this.unsubscribe$))
                    .subscribe((isSuccess: boolean) => {
                        this.massDownloadRequestSubject$.next(null);
                        this.progressDialog = null;
                        if (!isSuccess) {
                            this.httpCancelService.cancelPendingRequests();
                        }
                    });
            })
    }

    clearSearchFilter(): void {
        this.importMissingInvoiceNumberDocumentSearchCriteriaModel = new ImportMissingInvoiceNumberDocumentSearchCriteriaModel();
        this.isPickedSelectedItems = [];
        this.pageIndex = 0;
        this.search(true);
        this.storeFilters();
        this.onFiltersCleared.next();
    }

    onExport = () => {
        this.importService.searchMissingInvoiceNumberDocuments(this.sortColumn, this.sortDirection, 0, this.config.environnement.maxExportRows, this.importMissingInvoiceNumberDocumentSearchCriteriaModel)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((dataSource: ImportMissingInvoiceNumberDataSource) => {
                this.onExportDataSourceAvailable.emit(dataSource.Items);
            });
    }

    search(dataContentChanged: boolean) {
        this.importService.searchMissingInvoiceNumberDocuments(this.sortColumn, this.sortDirection, this.pageIndex, this.pageSize, this.importMissingInvoiceNumberDocumentSearchCriteriaModel)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((dataSource: ImportMissingInvoiceNumberDataSource) => {
                this.data = dataSource.Items;
                this.paginatorLength = dataSource.TotalItems;
                this.onDataSourceChanged.emit(dataContentChanged);
                this.storeFilters();
            });
    }

    inputChanged() {
        this.searchInputChanged.next('');
    }

    dateChangeSearch(result: any) {
      this.dateChange(result.type, result.selectedDate);
    }

    dateChange(type: string, selectedDate: any): void {
        var formated = !_.isNil(selectedDate) ? moment(selectedDate).format('YYYY-MM-DD') : null;

        switch (type) {
            case 'FromReceptionDate':
                this.importMissingInvoiceNumberDocumentSearchCriteriaModel.FromReceptionDate = formated;
                break;
            case 'ToReceptionDate':
                this.importMissingInvoiceNumberDocumentSearchCriteriaModel.ToReceptionDate = formated;
                break;
        }
        this.inputChanged();
    }

    onSortChange = (sort: Sort) => {
        if (sort && sort.active != 'Id' && sort.active != 'Box' && sort.active != 'Indication') {
            this.sortColumn = sort.active.includes('.') ? _.last(sort.active.split('.')) : sort.active;
            this.sortDirection = sort.direction;
            this.pageIndex = 0;
            this.search(true);
        }
    }

    onPageChange = (page: PageEvent) => {
        this.pageIndex = page.pageIndex;
        this.pageSize = page.pageSize;
        this.search(false);
    }

    onItemSelect() {
        this.importMissingInvoiceNumberDocumentSearchCriteriaModel.IsPickedStatus = this.isPickedSelectedItems && this.isPickedSelectedItems.length == 1
            ? (this.isPickedSelectedItems[0].Label == 'Oui') ? ImportMissingInvoiceNumberDocumentSearchCriteriaModelIsPickedStatuses.Picked : ImportMissingInvoiceNumberDocumentSearchCriteriaModelIsPickedStatuses.NotPicked : null;

        this.pageIndex = 0;

        this.search(true);
    }

    onSelectAll(items: any) {
        this.isPickedDropdownList = items;
        this.onItemSelect();
    }

    private restoreFilters(): void {
        const searchFilter = this.sessionStorageService.get<SearchFilterModel<ImportMissingInvoiceNumberDocumentSearchCriteriaModel>>(this.filterStorageKey);
        const pageNavigation = Object.assign({}, JSON.parse(localStorage.getItem(this.tablePreferenceConfigKey))?.pageDef) as TableNavigationModel;

        if (!_.isNil(pageNavigation.Sort)) {
            this.sortColumn = pageNavigation.Sort.active;
            this.sortDirection = pageNavigation.Sort.direction;
        }

        if (!_.isNil(pageNavigation.Page)) {
            this.pageSize = pageNavigation.Page.pageSize;
        }

        if (_.isNil(searchFilter)) {
            return;
        }

        this.importMissingInvoiceNumberDocumentSearchCriteriaModel = searchFilter.Filter;
        this.pageIndex = searchFilter.Page;

        if (!_.isNil(searchFilter.Filter.IsPickedStatus)) {
            if (searchFilter.Filter.IsPickedStatus === ImportMissingInvoiceNumberDocumentSearchCriteriaModelIsPickedStatuses.All) {
                this.isPickedSelectedItems = [...this.isPickedDropdownList];
            }
            else {
                const searchId = searchFilter.Filter.IsPickedStatus === ImportMissingInvoiceNumberDocumentSearchCriteriaModelIsPickedStatuses.Picked ? 1 : 0;
                this.isPickedSelectedItems = [...this.isPickedDropdownList.filter(r => r.Id === searchId)];
            }
        }

        if (!_.isNil(searchFilter.Filter.FromReceptionDate)) {
            this.importMissingInvoiceNumberDocumentSearchCriteriaModel.FromReceptionDate = searchFilter.Filter.FromReceptionDate
        }

        if (!_.isNil(searchFilter.Filter.ToReceptionDate)) {
            this.importMissingInvoiceNumberDocumentSearchCriteriaModel.ToReceptionDate = searchFilter.Filter.ToReceptionDate;
        }
    }

    private storeFilters(): void {
        var filter = new SearchFilterModel();
        filter.Filter = this.importMissingInvoiceNumberDocumentSearchCriteriaModel;
        filter.Page = this.pageIndex;
        sessionStorage.setItem(this.filterStorageKey, JSON.stringify(filter));
    }

    onZoom = (data: ImportMissingInvoiceNumberModel) => {
        const dialogModal = new ModalHelper().getImportMissingInvoiceNumberDocumentDetail(data, true);
        const dialogRef = this.dialog.open(AnalysisMissingInvoiceNumberDetailComponent, dialogModal);

        return dialogRef.afterClosed();
    }

    onPdfViewer = (data: ImportMissingInvoiceNumberModel) => {
        window.open(`/analysis/missing-invoice-numbers/detail-pdf/${data.Id}`, '_blank');
    }

    onDownload = (importDetail: ImportMissingInvoiceNumberModel) => {
        this.importService.getMissingInvoiceNumberDocumentContent(importDetail.Id)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((blobData: Blob) => {
                this.fileDownloadService.downloadBlob(blobData, importDetail.PdfFileName, "application/pdf");
            }, (_) => {
                const modalErreurConfig = new ModalHelper().getAlertConfig(`Une erreur est survenue lors du téléchargement.`);
                this.dialog.open(ModalComponent, modalErreurConfig);
            });
    }

    onServerSideAllDownload = () => {
        this.importService.searchMissingInvoiceNumberDocuments(this.sortColumn, this.sortDirection, 0, this.config.environnement.maxExportRows, this.importMissingInvoiceNumberDocumentSearchCriteriaModel)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((dataSource: ImportMissingInvoiceNumberDataSource) => {
                let massDownloadRequest = uuidv4();
                this.massDownloadRequestSubject$.next(massDownloadRequest);
                const documentsId = dataSource.Items
                    .filter(importDetail => !_.isNil(importDetail.DocumentId))
                    .map((importDetail) => importDetail.Id);
                this.importService.getMissingInvoiceNumberDocuments(documentsId, massDownloadRequest)
                    .pipe(takeUntil(this.unsubscribe$))
                    .subscribe((blobData: Blob) => {
                        this.massDownloadRequestSubject$.next(null);
                        let localDate = new Date().toLocaleDateString();
                        this.fileDownloadService.downloadBlob(blobData, `${this.configKey}_${localDate}.zip`, "application/zip");
                    }, _ => {
                        this.massDownloadRequestSubject$.next(null);
                        const modalErreurConfig = new ModalHelper().getAlertConfig(`Une erreur est survenue lors du téléchargement.`);
                        this.dialog.open(ModalComponent, modalErreurConfig);
                    });
            });
    }

    onRowSelectionChange = (tableRowsSelection: number[]) => {
        this.selectedIds = tableRowsSelection;
    }

    onAllRowSelectionChange = (isChecked: boolean) => {
        this.isAllSelected = isChecked;
    }

    onMassDownload = () => {
        if (this.isAllSelected === true) {
            this.onServerSideAllDownload();
            return;
        }

        if (this.selectedIds.length > 0) {
            let massDownloadRequest = uuidv4();
            this.massDownloadRequestSubject$.next(massDownloadRequest);
            this.importService.getMissingInvoiceNumberDocuments(this.selectedIds, massDownloadRequest)
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe((blobData: Blob) => {
                    this.massDownloadRequestSubject$.next(null);
                    let localDate = new Date().toLocaleDateString();
                    this.fileDownloadService.downloadBlob(blobData, `${this.configKey}_${localDate}.zip`, "application/zip");
                }, _ => {
                    this.massDownloadRequestSubject$.next(null);
                    const modalErreurConfig = new ModalHelper().getAlertConfig(`Une erreur est survenue lors du téléchargement.`);
                    this.dialog.open(ModalComponent, modalErreurConfig);
                });
        }
    }

    onMassPick = (missingInvoiceNumberDocumentIds: string[]) => {
        const importMissingInvoiceNumberDocuments = this.data.filter(i => missingInvoiceNumberDocumentIds.includes(i.Id.toString()));
        const someAreAlreadyPickedBySomeoneElse = importMissingInvoiceNumberDocuments.some(i => !_.isNil(i.PickedByUserId) && i.PickedByUserId !== this.authenticationInfoService.authentificatedUser.Id);
        if (someAreAlreadyPickedBySomeoneElse) {
            const confimModal = new ModalHelper().getConfirmationConfig(`Attention, certains documents sont affectés à quelqu'un d'autre. Voulez-vous quand même procéder ?`);
            const dialogRef = this.dialog.open(ModalComponent, confimModal);
            return dialogRef.afterClosed()
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe((isSuccess: boolean) => {
                    if (isSuccess) {
                        return this.massPick(missingInvoiceNumberDocumentIds);
                    }
                });
        }

        return this.massPick(missingInvoiceNumberDocumentIds);
    };

    massPick = (missingInvoiceNumberDocumentIds: string[]) => {
        const ids = missingInvoiceNumberDocumentIds.map(id => _.toNumber(id));
        this.importService.pickImportMissingInvoiceNumberDocuments(ids)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                const date = new Date();
                this.data.filter(i => ids.includes(i.Id)).forEach(document => {
                    document.PickedByUserName = this.authenticationInfoService.authentificatedUser.FullName;
                    document.PickedByUserId = this.authenticationInfoService.authentificatedUser.Id;
                    document.PickedAt = date;
                })
            });
    }

    onMassUnpick = (missingInvoiceNumberDocumentIds: string[]) => {
        const ids = missingInvoiceNumberDocumentIds.map(id => _.toNumber(id));
        this.importService.unpickImportMissingInvoiceNumberDocuments(ids)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => {
                this.data.filter(i => ids.includes(i.Id)).forEach(document => {
                    document.PickedByUserName = null;
                    document.PickedByUserId = null;
                    document.PickedAt = null;
                })
            });
    };

    ensureAllArePickedByMe = (importMissingInvoiceNumberDocumentsId: string[]) => {
        const importMissingInvoiceNumberDocuments = this.data.filter(i => importMissingInvoiceNumberDocumentsId.includes(i.Id.toString()));
        return !importMissingInvoiceNumberDocuments.some(i => _.isNil(i.PickedByUserId) || i.PickedByUserId !== this.authenticationInfoService.authentificatedUser.Id)
    };

    ensureCanBeInjected = (data: ImportMissingInvoiceNumberModel): boolean => {
        return _.isNil(data.InjectedInvoiceId);
    }

    onInject = (data: ImportMissingInvoiceNumberModel) => {
        this.router.navigate(['invoices/inject', data.Id]);
    }

    ngOnDestroy() {
        this.unsubscribe$.next(true);
    }

    exportAsExcel() : void {
      this.onExportExcelCaller.next();
    }

    onShowFilter(panelvalue:boolean) {
      this.panelOpenState = panelvalue;
    }
}
